import Ionicons from "@expo/vector-icons/Ionicons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";

import { chatApi } from "@/api";
import { ThemedText } from "@/components/themed-text";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import { KeyboardAvoidingContainer } from "@/components/ui/keyboard-view";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { notify } from "@/lib/dialog";
import type { TranslationKey } from "@/i18n";
import type { ReportReason } from "@/types";

const REASONS: { value: ReportReason; labelKey: TranslationKey }[] = [
  { value: "harassment", labelKey: "profile.report.reasons.harassment" },
  {
    value: "inappropriate_messages",
    labelKey: "profile.report.reasons.messages",
  },
  { value: "sexual_content", labelKey: "profile.report.reasons.sexual" },
  { value: "hate_speech", labelKey: "profile.report.reasons.hate" },
  { value: "threats", labelKey: "profile.report.reasons.threats" },
  { value: "spam_scam", labelKey: "profile.report.reasons.spam" },
  { value: "fake_profile", labelKey: "profile.report.reasons.fake" },
  { value: "underage", labelKey: "profile.report.reasons.underage" },
  { value: "unsafe_in_person", labelKey: "profile.report.reasons.unsafe" },
  { value: "other", labelKey: "profile.report.reasons.other" },
];

/**
 * The report form. Lets someone report a person for safety/policy reasons and
 * choose what protective action to take. The reported person is never notified.
 */
export function ReportModal({
  visible,
  reportedUserId,
  chatId,
  photoAssetId,
  onClose,
  onSubmitted,
}: {
  visible: boolean;
  reportedUserId: string;
  chatId?: string;
  /**
   * Set when the report is about one exchanged photo. Passing it hides that
   * photo from the reporter the moment the report is filed, rather than
   * whenever a moderator gets to it.
   */
  photoAssetId?: string;
  onClose: () => void;
  onSubmitted?: () => void;
}) {
  const { t } = useTranslation();
  const c = useTheme();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState("");
  const [block, setBlock] = useState(true);
  const [endConversation, setEndConversation] = useState(true);
  const [hide, setHide] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Both replace a `notify()` that used to be raised over this open Modal.
  const [missingReason, setMissingReason] = useState(false);

  // Re-initialise each time it opens (the host keeps it mounted).
  useEffect(() => {
    if (!visible) return;
    setReason(null);
    setDetails("");
    setBlock(true);
    setEndConversation(true);
    setHide(false);
    setSubmitting(false);
    setMissingReason(false);
  }, [visible]);

  async function submit() {
    // Inline, not a dialog. This is the one case where the person must fix
    // something in THIS form, so closing it to ask would throw away what they
    // had typed — and raising a dialog over an open Modal is the iOS wedge
    // this whole file was audited for.
    if (!reason) {
      setMissingReason(true);
      return;
    }
    if (submitting || !reportedUserId) return;
    setSubmitting(true);
    try {
      await chatApi.report({
        reportedUserId,
        chatId,
        reason,
        details: details.trim() || undefined,
        photoAssetId,
        block,
        endConversation,
        hide,
      });
      // Close BEFORE the notice, not after: `notify` is the shared
      // ConfirmDialog, which is a native Modal, and iOS will not present one
      // over this Modal — the notice never appears and its invisible window
      // eats every touch. The report is sent; this form has nothing left to
      // show either way.
      onClose();
      notify(
        t("profile.report.submittedTitle"),
        t("profile.report.submittedBody"),
      );
      onSubmitted?.();
    } catch {
      onClose();
      notify(t("profile.report.failedTitle"), t("profile.report.failedBody"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      statusBarTranslucent
      onRequestClose={onClose}
    >
      <View style={styles.screen}>
        <BlurredBackdrop
          accessibilityLabel={t("profile.report.closeA11y")}
          style={styles.backdrop}
          onPress={onClose}
        />

        <KeyboardAvoidingContainer style={styles.sheetWrap}>
          <View
            style={[
              styles.sheet,
              { backgroundColor: c.background, borderColor: c.border },
            ]}
          >
            <View style={styles.header}>
              <ThemedText variant="title" style={styles.headerTitle}>
                {t("profile.report.title")}
              </ThemedText>
              <Pressable
                onPress={onClose}
                hitSlop={10}
                accessibilityLabel={t("common.close")}
                style={styles.headerClose}
              >
                <Ionicons name="close" size={24} color={c.textSecondary} />
              </Pressable>
            </View>

            <ThemedText variant="body" tone="secondary">
              {t("profile.report.body")}
            </ThemedText>

            <ScrollView
              style={styles.scroll}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.content}
            >
              <SectionLabel text={t("profile.report.reasonLabel")} />
              <View style={styles.reasons}>
                {REASONS.map((r) => {
                  const active = reason === r.value;
                  return (
                    <Pressable
                      key={r.value}
                      accessibilityRole="radio"
                      accessibilityState={{ selected: active }}
                      onPress={() => {
                        setReason(r.value);
                        setMissingReason(false);
                      }}
                      style={[
                        styles.reasonRow,
                        {
                          borderColor: active ? c.tint : c.border,
                          backgroundColor: active
                            ? c.backgroundSelected
                            : c.surfaceAlt,
                        },
                      ]}
                    >
                      <ThemedText
                        variant="body"
                        tone={active ? "tint" : "default"}
                      >
                        {t(r.labelKey)}
                      </ThemedText>
                      <Ionicons
                        name={active ? "radio-button-on" : "radio-button-off"}
                        size={18}
                        color={active ? c.tint : c.textMuted}
                      />
                    </Pressable>
                  );
                })}
              </View>

              {missingReason ? (
                <ThemedText variant="body" style={{ color: c.danger }}>
                  {t("profile.report.chooseReasonBody")}
                </ThemedText>
              ) : null}

              <SectionLabel text={t("profile.report.whatHappened")} />
              <TextInput
                value={details}
                onChangeText={(t) => setDetails(t.slice(0, 2000))}
                placeholder={t("profile.report.detailsPlaceholder")}
                placeholderTextColor={c.textMuted}
                multiline
                style={[
                  styles.details,
                  {
                    color: c.text,
                    backgroundColor: c.surfaceAlt,
                    borderColor: c.border,
                  },
                ]}
              />

              <SectionLabel text={t("profile.report.addEvidence")} />
              <SectionLabel text={t("profile.report.nextAction")} />
              <CheckRow
                label={t("profile.report.block")}
                checked={block}
                onToggle={() => setBlock((v) => !v)}
                c={c}
              />
              <CheckRow
                label={t("profile.report.endConversation")}
                checked={endConversation}
                onToggle={() => setEndConversation((v) => !v)}
                c={c}
              />
              <CheckRow
                label={t("profile.report.hide")}
                checked={hide}
                onToggle={() => setHide((v) => !v)}
                c={c}
              />
            </ScrollView>

            <View style={styles.actions}>
              <Pressable
                onPress={submit}
                disabled={submitting}
                style={[
                  styles.submit,
                  { backgroundColor: c.danger, opacity: submitting ? 0.7 : 1 },
                ]}
              >
                <ThemedText variant="bodyStrong" tone="onTint">
                  {t(
                    submitting
                      ? "profile.report.submitting"
                      : "profile.report.submit",
                  )}
                </ThemedText>
              </Pressable>
              <Pressable onPress={onClose} style={styles.cancel}>
                <ThemedText variant="bodyStrong" tone="muted">
                  {t("common.cancel")}
                </ThemedText>
              </Pressable>
            </View>
          </View>
        </KeyboardAvoidingContainer>
      </View>
    </Modal>
  );
}

function SectionLabel({ text }: { text: string }) {
  return (
    <ThemedText variant="label" tone="secondary" style={styles.sectionLabel}>
      {text}
    </ThemedText>
  );
}

function CheckRow({
  label,
  checked,
  onToggle,
  c,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
  c: ReturnType<typeof useTheme>;
}) {
  return (
    <Pressable
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      onPress={onToggle}
      style={styles.checkRow}
    >
      <Ionicons
        name={checked ? "checkbox" : "square-outline"}
        size={22}
        color={checked ? c.tint : c.textMuted}
      />
      <ThemedText variant="body">{label}</ThemedText>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, justifyContent: "flex-end" },
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(8, 15, 26, 0.5)",
  },
  sheetWrap: { width: "100%" },
  sheet: {
    maxHeight: "88%",
    padding: Spacing.lg,
    gap: Spacing.sm,
    borderTopLeftRadius: Radius.xl,
    borderTopRightRadius: Radius.xl,
    borderWidth: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  // Same defect as the poster preview header, found by the same scan: React
  // Native's `flexShrink` default of 0 lets a long title claim the whole row
  // and push the close button off the right edge at a large OS text setting.
  // This sheet has a backdrop to tap, so it was never a trap — but reporting
  // somebody is not a screen to leave people guessing on.
  headerTitle: { flexShrink: 1, minWidth: 0 },
  headerClose: { flexShrink: 0 },
  // Let the list take the remaining space and scroll internally so the header
  // and actions stay pinned and the top options are never pushed off-screen.
  scroll: { flexShrink: 1, flexGrow: 0 },
  content: { gap: Spacing.sm, paddingBottom: Spacing.md },
  sectionLabel: { marginTop: Spacing.md },
  reasons: { gap: Spacing.xs },
  reasonRow: {
    minHeight: 46,
    paddingHorizontal: Spacing.md,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  details: {
    minHeight: 90,
    padding: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
    fontSize: 15,
    textAlignVertical: "top",
  },
  checkRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    paddingVertical: Spacing.sm,
  },
  actions: { gap: Spacing.xs, marginTop: Spacing.sm },
  submit: {
    minHeight: 52,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
  },
  cancel: { minHeight: 44, alignItems: "center", justifyContent: "center" },
});
